import React from "react";
import ReactDOM from "react-dom";
import { useTheme } from "styled-components";
import { deprecationWarning } from "../../utils/warning";
import { MatchMedia, MatchMediaTheme, QueryList, Viewport } from "../types";

export type MediaQueryWrapper = {
  /**
   * A styled-components type function which can returns the media query.
   */
  (props: { theme: MatchMediaTheme }): string;

  /**
   * A React hook which returns whether the current breakpoint matches the media query.
   */
  useMatchesBreakpoint(): boolean;

  /**
   * Metadata for the media query.
   */
  meta: {
    /**
     * The value of the media query
     */
    value: string;
    /**
     * The viewport dimensions used to initialize the media query
     */
    viewport: Viewport;

    /**
     * An identifier for the viewport.
     */
    viewportId: string;
  };
};

/**
 * A cache of all QueryList objects so that if multiple `useMatchesBreakpoint()` hooks are used for the same
 * breakpoint, we can batch DOM updates together.
 */
const queryListCache = new Map<string, QueryList>();

const createQueryList = (value: string): QueryList => {
  const subscribers = new Set<() => void>();

  /**
   * This function will batch all DOM updates for a given breakpoint together.
   */
  const notify = () => ReactDOM.unstable_batchedUpdates(() => subscribers.forEach((cb) => cb()));

  /**
   * `matchMedia` is not defined in JS DOM.
   */
  const queryList = window?.matchMedia?.(value) ?? { matches: false };

  return {
    get matches() {
      return queryList?.matches ?? false;
    },

    subscribe(sub: () => void) {
      subscribers.add(sub);

      if (subscribers.size === 1) {
        queryList?.addEventListener?.("change", notify);
      }

      return () => {
        subscribers.delete(sub);

        if (subscribers.size === 0) {
          queryList?.removeEventListener?.("change", notify);
        }
      };
    },
  };
};

const defaultMatchMedia: MatchMedia = (value: string) => {
  let queryList = queryListCache.get(value);

  if (!queryList) {
    queryList = createQueryList(value);
    queryListCache.set(value, queryList);
  }

  return queryList;
};

const deriveMediaQuery = (value: Viewport): string => {
  if (typeof value.min === "number" && typeof value.max === "number") {
    return `(min-width: ${value.min}px) and (max-width: ${value.max}px)`;
  } else if (typeof value.min === "number") {
    return `(min-width: ${value.min}px)`;
  } else if (typeof value.max === "number") {
    return `(max-width: ${value.max}px)`;
  } else {
    /**
     * By the type definition of Viewport, this should never happen.
     */
    throw new Error("Unexpected Error. Invalid Viewport.");
  }
};

/**
 * Creates an object that can be used as a media query for styled-components
 * and exposes a member `useMatchesBreakpoint()`.
 *
 * This allows us to "force" a specific breakpoint in JavaScript by injecting a styled-components
 * theme with a function `_matchMedia` that fakes whether the media query matches the current breakpoint.
 */
export const createMediaQuery = (viewport: Viewport, viewportId: string): MediaQueryWrapper => {
  const value = deriveMediaQuery(viewport);

  /**
   * A styled-components type function which can returns the media query.
   * In test mode, it falls back to the `_matchMedia` function so that media queries can be mocked out.
   */
  const fn = function mediaQuery(props: { theme: MatchMediaTheme }): string {
    if (process.env.NODE_ENV === "test") {
      /**
       * This block is compiled away in production.
       */
      const matchMedia = props.theme?._matchMedia ?? defaultMatchMedia;

      if (matchMedia(value, viewport).matches) {
        return `@media ${value}`;
      } else {
        /**
         * If `matchMedia().matches` returns false then fake a false condition by returning
         * a media query that can never be satisfied.
         */
        return `@media (max-width: 0px) and (min-width: 1px)`;
      }
    }

    return `@media ${value}`;
  };

  fn.useMatchesBreakpoint = function useMatchesBreakpoint() {
    React.useDebugValue(value);

    /**
     * We can force the hook to re-render the component by calling `forceUpdate()`.
     * Give the components an opportunity to re-check the value of `queryList.matches`.
     */
    const [, forceUpdate] = React.useReducer((prev) => prev + 1, 0);

    /**
     * Fetch the theme and if the theme does not have the _matchMedia function,
     * then fallback to the browser default.
     */
    const theme = useTheme() as MatchMediaTheme;
    const matchMedia = theme?._matchMedia ?? defaultMatchMedia;

    const queryList = React.useMemo(() => matchMedia(value, viewport), [matchMedia]);

    React.useEffect(() => queryList.subscribe(forceUpdate), [queryList]);

    return queryList.matches;
  };

  fn.meta = Object.freeze({
    value,
    viewport,
    viewportId,
  });

  /**
   * Doing this will remind developers that any css blocks they use in styled-components must be tagged with
   * the `css` function; otherwise, they cannot take advantage of injecting matchMedia.
   *
   * That is, the following is a regular string and so `mediaQueries.large` will be transformed into a string _immediately_.
   *
   * styled.div`
   *   ${props =>
   *     `${mediaQueries.large} {
   *       color: blue;
   *     }`}
   * `
   *
   * But the following is a string that is tagged with `css` and so the function is deferred until the theme can be injected.
   *
   * styled.div`
   *   ${props =>
   *     css`${mediaQueries.large} {
   *       color: blue;
   *     }`}
   * `
   */
  fn.toString = () => {
    deprecationWarning(
      `Interpolating media queries in a plain string (that is, \`\${mediaQueries.${viewportId}}\`)`,
      `css\`\${mediaQueries.${viewportId}}\``
    );
    return `@media ${value}`;
  };

  return Object.freeze(fn) as MediaQueryWrapper;
};
